import { ServiceItem, ServiceTransaction } from '../types';
import { isHourlyUnit } from './serviceUnits';

/**
 * Die Buchung einer SiGe-Plan-EINWEISUNG auf eine LV-Position — bewusst als
 * pure Funktion neben der Oberflaeche.
 *
 * WARUM UEBERHAUPT: dieselbe Buchung steht heute in jedem Modul noch einmal
 * inline (SiGePlanEditor.executePdfDownload ab Zeile 531 zeigt das Muster:
 * quantityUsed erhoehen, Status auf `done`, Log-Eintrag bauen, alles ueber
 * onUpdate ins Projekt schreiben). Jede Abschrift ist eine Stelle, an der eine
 * spaetere Korrektur vergessen wird — und Fehler in diesem Pfad kosten Geld,
 * nicht Kosmetik. Die Einweisungs-Oberflaeche schreibt das deshalb nicht noch
 * einmal ab, sondern ruft hier.
 *
 * WARUM PURE: das Projekt hat keine DOM-Testumgebung (stehende Entscheidung
 * „Logik in Hooks + pure Funktionen"). Die Funktion liest und schreibt nichts,
 * sie gibt die NEUE Leistungsliste und den Log-Eintrag zurueck; der Aufrufer
 * legt beides ueber seinen vorhandenen onUpdate-Pfad ab.
 *
 * FACHLICH: eine Einweisung wird auf einer Baustelle MEHRFACH gehalten. Sie
 * rechnet deshalb wie eine Begehung ueber ein Kontingent ab (Einheit „Stück",
 * Standardposition 2.12.10 mit 3 Stück) und nicht wie die Pauschalpositionen
 * SiGe-Plan, Vorankuendigung und Unterlage.
 */

/**
 * Der Eintrag in `globalData.serviceLog`. In types.ts heisst der Typ
 * `ServiceTransaction`; unter diesem Namen ist er hier gefuehrt, weil die
 * Aufrufer ihn als Log-Eintrag kennen. Reines Alias — die FELDNAMEN bleiben
 * exakt die des Bestands, sonst verstehen Cockpit (AdminDashboard) und
 * Auswertungen den Eintrag nicht mehr.
 */
export type ServiceLogEntry = ServiceTransaction;

/** Was der Log-Text ueber die gebuchte Einweisung sagen soll. */
export interface EinweisungBuchungsKontext {
    /** Laufende Nummer der Einweisung je Projekt (SigeEinweisung.nummer). */
    nummer: number;
    /** Tag der Einweisung, ISO (`YYYY-MM-DD`) — wie SigeEinweisung.datum. */
    datum?: string;
    /** Eingefrorene Plan-Nr. des Planbezugs (SigeEinweisung.plan_nr). */
    planNr?: string;
    /** `papier` wird im Text ausdruecklich benannt, siehe unten. */
    quelle: 'digital' | 'papier';
    /** Anzeigename des Durchfuehrenden; ohne ihn steht „System" im Protokoll. */
    performerName?: string;
}

export interface EinweisungBuchung {
    /** Die vollstaendige Leistungsliste MIT der gebuchten Position. */
    services: ServiceItem[];
    /** Der Eintrag, der an `globalData.serviceLog` angehaengt wird. */
    logEntry: ServiceLogEntry;
}

/**
 * ISO-Datum (`YYYY-MM-DD`) in deutsche Schreibweise, sonst wortgetreu.
 *
 * Bewusst per Zeichenkette und NICHT ueber `new Date(...)`: ein reines
 * Tagesdatum wird dort als UTC-Mitternacht gelesen und rutscht in einer
 * Zeitzone westlich von Greenwich einen Tag zurueck. Das Protokoll soll den
 * Tag nennen, an dem eingewiesen wurde, nicht den davor.
 */
const formatiereDatum = (iso?: string): string => {
    const text = (iso || '').trim();
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(text);
    return m ? `${m[3]}.${m[2]}.${m[1]}` : text;
};

/**
 * Der Protokolltext einer gebuchten Einweisung.
 *
 * Er nennt die laufende NUMMER (der Bezug zum Blatt mit den Unterschriften),
 * das DATUM und die PLAN-NR. — ohne sie liesse sich eine Buchung im Nachhinein
 * keiner Einweisung mehr zuordnen. Bei einer in PAPIERFORM nachgetragenen
 * Einweisung steht die Herkunft ausdruecklich dabei: dort liegt der Nachweis
 * als Scan im Bucket und nicht im System, und wer die Buchung spaeter prueft,
 * muss das aus dem Protokoll erkennen koennen, ohne den Datensatz zu oeffnen.
 *
 * Form wie im Bestand: Beschreibung, Klammerzusatz, dann die gebuchte Menge
 * mit ihrer Einheit.
 */
const buildDescription = (
    kontext: EinweisungBuchungsKontext,
    menge: number,
    unit: string,
): string => {
    const teile: string[] = [`Nr. ${kontext.nummer}`];

    const datum = formatiereDatum(kontext.datum);
    if (datum) teile.push(datum);

    const planNr = (kontext.planNr || '').trim();
    teile.push(`Plan-Nr. ${planNr || 'ohne'}`);

    if (kontext.quelle === 'papier') teile.push('in Papierform');

    return `Einweisung SiGe-Plan (${teile.join(', ')}) (${menge} ${unit})`;
};

/**
 * Bucht eine Einweisung auf die LV-Position `serviceId`.
 *
 * `null`, wenn es die Position nicht (mehr) gibt — der Aufrufer darf dann NICHT
 * schreiben. Ein stilles „dann eben nichts buchen, aber die Einweisung als
 * abgerechnet markieren" waere die teuerste Variante: die Leistung waere
 * erbracht, aus dem Kontingent verschwunden und stuende auf keiner Rechnung.
 *
 * MENGE: bei einer Stundenposition sind es die Stunden, sonst die Stueckzahl.
 * Beides ist `menge` — anders als der SiGe-Plan-Export, der fuer eine
 * Stueckposition fest eine 1 bucht. Der Export erzeugt genau EIN Dokument,
 * eine Einweisungsbuchung traegt dagegen die Zahl aus dem Abrechnungsdialog
 * (eine nachgetragene Papier-Einweisung kann mehrere Termine zusammenfassen).
 * Eine feste 1 waere hier ein Parameter, der ignoriert wird — also eine stille
 * Unterbuchung. Nur eine unbrauchbare Zahl (0, negativ, NaN) faellt auf 1
 * zurueck, damit eine Buchung nie mit 0 im Protokoll landet.
 *
 * STATUS: `done`, sobald das Kontingent erreicht ODER ueberschritten ist —
 * genau die Regel des Bestands. Ein Ueberschreiten wird nicht gedeckelt: der
 * Leistungsstand soll die Wahrheit zeigen, die Ueberdeckung faellt in der
 * Rechnung auf und nicht hier.
 */
export const bucheEinweisung = (
    services: ServiceItem[],
    serviceId: string,
    menge: number,
    kontext: EinweisungBuchungsKontext,
): EinweisungBuchung | null => {
    const index = (services || []).findIndex(s => s.id === serviceId);
    if (index === -1) return null;

    const service = services[index];
    const brauchbar = Number.isFinite(menge) && menge > 0;
    const increment = brauchbar ? menge : 1;
    // isHourlyUnit entscheidet nur ueber die BEDEUTUNG der Zahl (Stunden statt
    // Stueck) — gebucht wird in beiden Faellen dieselbe Menge. Der Aufruf steht
    // hier trotzdem, damit der Protokolltext die richtige Einheit nennt.
    const unit = isHourlyUnit(service.unit) ? (service.unit || 'Stunden') : (service.unit || 'Stück');

    const newUsed = (service.quantityUsed || 0) + increment;
    const isDone = service.quantityContracted > 0 && newUsed >= service.quantityContracted;

    const newServices = [...services];
    newServices[index] = {
        ...service,
        quantityUsed: newUsed,
        status: isDone ? 'done' : service.status,
    };

    const logEntry: ServiceLogEntry = {
        id: crypto.randomUUID(),
        date: new Date().toISOString(),
        serviceId: service.id,
        quantity: increment,
        performerId: 'system',
        performerName: kontext.performerName || 'System',
        isSubstitution: false,
        description: buildDescription(kontext, increment, unit),
    };

    return { services: newServices, logEntry };
};
